import { DateTime } from 'luxon';

import HoverCopyableField from 'dashboard/components/HoverCopyableField';
import TransactionStatusSticker from 'dashboard/components/TransactionStatusSticker';
import { MonoSpan } from 'dashboard/components/Typography';
import TableLayout, { TableLinkTR, TableTD, TableTH } from 'dashboard/layouts/TableLayout';
import { TableNullState } from 'dashboard/layouts/TableLayoutV2';
import { SortParams } from 'dashboard/lib/interfaces/SortParams';
import { Transaction } from 'dashboard/models/transaction';
import { TransactionStatus } from 'dashboard/models/transaction-status';
import { TransactionTypeFilterOption } from 'dashboard/models/transaction-type';
import Spacer from 'design-system/components/Spacer';
import NullStateTransactions from 'images/dashboard/i16-briefcase.svg';
import { DATE_AND_TIME_FORMAT, TIMEZONE_NAME_ABBREVIATED } from 'lib/constants/luxonFormats';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

interface TransactionsTableProps {
  hasUsedFilters: boolean;
  loading: boolean;
  transactions: Transaction[];
  transactionStatuses: TransactionStatus[];
  transactionTypeFilterOptions: TransactionTypeFilterOption[];
  sortParams: SortParams;
  dataTest?: string;
  handleSortUpdate: (sortParams: SortParams) => void;
}

export default function TransactionsTable({
  hasUsedFilters,
  loading,
  transactions = [],
  transactionStatuses = [],
  transactionTypeFilterOptions = [],
  sortParams,
  handleSortUpdate,
}: TransactionsTableProps) {
  const preferExternalIdEnabled = useFeatureFlag('dashboard:txn-prefer-external-id');

  const handleColumnHeaderClick = (columnName: string) => {
    handleSortUpdate({
      sort: columnName,
      shouldSortDescending:
        columnName === sortParams.sort ? !sortParams.shouldSortDescending : true,
    });
  };

  const transactionRows = transactions.map((transaction) => {
    const transactionStatus = transactionStatuses.find(
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      (ts) => transaction.relationships['transaction-status'].data.id === ts.id,
    );
    const transactionTypeFilterOption = transactionTypeFilterOptions.find(
      (filterOption) => transaction.relationships['transaction-type'].data.id === filterOption.id,
    );

    return (
      <TransactionTableRow
        key={transaction.id}
        transaction={transaction}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        transactionTypeFilterOption={transactionTypeFilterOption}
        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
        transactionStatus={transactionStatus}
      />
    );
  });

  return (
    <TableLayout
      dataTest='transactions-table__table'
      isLoading={loading}
      isEmpty={transactions.length === 0}
      showHeader={hasUsedFilters}
      headerTR={
        <>
          <TableTH id='transactions-table__header--id'>ID</TableTH>
          {preferExternalIdEnabled ? (
            <TableTH id='transactions-table__header--external-id'>External ID</TableTH>
          ) : (
            <TableTH id='transactions-table__header--reference-id'>Reference ID</TableTH>
          )}
          <TableTH>Type</TableTH>
          <TableTH
            canSort={true}
            isSorted={sortParams.sort === 'created_at'}
            isDescending={sortParams.shouldSortDescending}
            id='transactions-table__header--created-at'
            onClick={() => handleColumnHeaderClick('created_at')}
          >
            {`Created at (${DateTime.local().toFormat(TIMEZONE_NAME_ABBREVIATED)})`}
          </TableTH>
          <TableTH id='transactions-table__header--status'>Status</TableTH>
        </>
      }
      nullState={
        <TableNullState hasUsedFilters={hasUsedFilters} dataTestBlock='transactions-view'>
          <NullStateTransactions />
          <Spacer height={3} />
          <div>Transactions mirror the interactions in your system.</div>
        </TableNullState>
      }
    >
      {transactionRows}
    </TableLayout>
  );
}

interface TransactionTableRowProps {
  transaction: Transaction;
  transactionStatus: TransactionStatus;
  transactionTypeFilterOption: TransactionTypeFilterOption;
}

const TransactionTableRow = ({
  transaction,
  transactionStatus,
  transactionTypeFilterOption,
}: TransactionTableRowProps) => {
  const preferExternalIdEnabled = useFeatureFlag('dashboard:txn-prefer-external-id');

  return (
    <TableLinkTR
      to={`/dashboard/transactions/${transaction.id}`}
      data-test={`transactions-table-row__${transaction.id}`}
      id={`transactions-table-row__${transaction.id}`}
    >
      <TableTD>
        <HoverCopyableField copyValue={transaction.id} confirmationText='Copied Transaction ID'>
          <MonoSpan>{transaction.id}</MonoSpan>
        </HoverCopyableField>
      </TableTD>
      <TableTD>
        {preferExternalIdEnabled ? (
          <HoverCopyableField
            copyValue={transaction.attributes['external-id']}
            confirmationText='Copied Reference ID'
          >
            <MonoSpan>{transaction.attributes['external-id']}</MonoSpan>
          </HoverCopyableField>
        ) : (
          <HoverCopyableField
            copyValue={transaction.attributes['reference-id']}
            confirmationText='Copied External ID'
          >
            <MonoSpan>{transaction.attributes['reference-id']}</MonoSpan>
          </HoverCopyableField>
        )}
      </TableTD>
      <TableTD>{transactionTypeFilterOption?.name}</TableTD>
      <TableTD>
        {DateTime.fromISO(transaction.attributes['created-at']).toFormat(DATE_AND_TIME_FORMAT)}
      </TableTD>
      <TableTD>
        <TransactionStatusSticker transactionStatus={transactionStatus} />
      </TableTD>
    </TableLinkTR>
  );
};
